/**
 * Dawr - Branding Constants
 * 
 * Centralized branding configuration for the global automotive service discovery platform
 */

export const BRAND = {
  // Company Information
  name: 'Dawr',
  fullName: 'Dawr',
  company: 'GADA',
  companyFull: 'GADA — Global Automotive Discovery Application',
  tagline: 'Trusted Places. Skilled People. Reliable Services.',
  description: 'Discover, book, and review automotive services worldwide',
  
  // Localized Names
  nameAr: 'Dawr',
  fullNameAr: 'Dawr',
  taglineAr: 'أماكن موثوقة. أشخاص ماهرون. خدمات يُعتمد عليها.',
  descriptionAr: 'اكتشف، احجز، وقيّم خدمات السيارات حول العالم',
  
  // Domain and URLs
  domain: 'dawr.ai',
  website: 'https://dawr.ai',
  
  // Social Media
  social: {
    twitter: '@DawrApp',
    instagram: '@dawrapp',
    linkedin: 'dawr',
    facebook: 'DawrApp',
  },
  
  // Contact Information
  contact: {
    email: 'support@dawr.ai',
    business: 'hello@dawr.ai',
    dpo: 'privacy@dawr.ai',
    phone: '', // Configure per region
    whatsapp: '', // Configure per region
  },
} as const;

export const AUTOMOTIVE_THEMES = {
  // Primary Color Palette
  colors: {
    primary: {
      // Petrol Blue - Main brand color
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9', // Main brand blue
      600: '#0284c7', // Primary button color
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
    secondary: {
      // Metallic Grey - Accent color
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b', // Main metallic grey
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    accent: {
      // Chrome Yellow - Highlights and premium features
      50: '#fefce8',
      100: '#fef9c3',
      200: '#fef08a',
      300: '#fde047',
      400: '#facc15',
      500: '#eab308', // Warning/premium color
      600: '#ca8a04',
      700: '#a16207',
      800: '#854d0e',
      900: '#713f12',
    },
    emergency: {
      // Performance Red - Emergency services
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444', // Error/emergency color
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    success: {
      // Racing Green - Success states
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e', // Success color
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
  },
  
  // Typography
  typography: {
    fontFamily: {
      primary: '"Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
      arabic: '"Noto Sans Arabic", "Cairo", "Tahoma", sans-serif',
      display: '"Poppins", "Inter", sans-serif',
      mono: '"JetBrains Mono", "Monaco", "Consolas", monospace',
    },
    weights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
  
  // Spacing and Layout
  layout: {
    borderRadius: {
      small: '6px',
      medium: '8px',
      large: '12px',
      pill: '9999px',
    },
    shadows: {
      light: '0 1px 3px rgba(0, 0, 0, 0.1)',
      medium: '0 4px 6px rgba(0, 0, 0, 0.1)',
      large: '0 10px 15px rgba(0, 0, 0, 0.1)',
      automotive: '0 4px 15px rgba(14, 165, 233, 0.2)', // Brand blue shadow
    },
  },
} as const;

export const SERVICE_CATEGORIES = {
  // Quick mapping from category IDs to display info
  maintenance: {
    name: 'Maintenance & Repair',
    nameAr: 'الصيانة والإصلاح',
    color: AUTOMOTIVE_THEMES.colors.primary[600],
    icon: 'build',
  },
  tires: {
    name: 'Tires & Wheels',
    nameAr: 'الإطارات والعجلات',
    color: AUTOMOTIVE_THEMES.colors.secondary[600],
    icon: 'tire_repair',
  },
  wash: {
    name: 'Car Wash & Detailing',
    nameAr: 'غسيل السيارات والتفصيل',
    color: AUTOMOTIVE_THEMES.colors.primary[500],
    icon: 'local_car_wash',
  },
  emergency: {
    name: 'Emergency Services',
    nameAr: 'الخدمات الطارئة',
    color: AUTOMOTIVE_THEMES.colors.emergency[600],
    icon: 'emergency',
  },
  government: {
    name: 'Government Services',
    nameAr: 'الخدمات الحكومية',
    color: AUTOMOTIVE_THEMES.colors.secondary[700],
    icon: 'account_balance',
  },
} as const;

export const UI_TEXT = {
  // Common UI strings with automotive context
  common: {
    search: 'Search Services',
    searchAr: 'البحث عن الخدمات',
    filter: 'Filter Services',
    filterAr: 'تصفية الخدمات',
    location: 'Location',
    locationAr: 'الموقع',
    categories: 'Service Categories',
    categoriesAr: 'فئات الخدمات',
    featured: 'Featured Services',
    featuredAr: 'الخدمات المميزة',
    emergency: 'Emergency Services',
    emergencyAr: 'الخدمات الطارئة',
    bookNow: 'Book Now',
    bookNowAr: 'احجز الآن',
    callNow: 'Call Now',
    callNowAr: 'اتصل الآن',
    directions: 'Get Directions',
    directionsAr: 'الحصول على الاتجاهات',
    reviews: 'Reviews',
    reviewsAr: 'التقييمات',
    rating: 'Rating',
    ratingAr: 'التقييم',
    openNow: 'Open Now',
    openNowAr: 'مفتوح الآن',
    verified: 'Verified Service',
    verifiedAr: 'خدمة موثقة',
  },
  
  // Page titles with automotive focus
  pages: {
    home: 'Discover Services',
    homeAr: 'اكتشف الخدمات',
    search: 'Search Services',
    searchAr: 'البحث عن خدمات السيارات',
    profile: 'Service Provider Profile',
    profileAr: 'ملف مقدم الخدمة',
    booking: 'Book Service',
    bookingAr: 'حجز الخدمة',
    reviews: 'Service Reviews',
    reviewsAr: 'تقييمات الخدمة',
  },
  
  // Placeholders
  placeholders: {
    searchServices: 'Search for car services, mechanics, garages...',
    searchServicesAr: 'البحث عن خدمات السيارات، الميكانيكيين، الكراجات...',
    location: 'Enter your city or area',
    locationAr: 'أدخل مدينتك أو منطقتك',
    reviews: 'Share your experience with this service...',
    reviewsAr: 'شارك تجربتك مع هذه الخدمة...',
  },
  
  // Error messages
  errors: {
    serviceNotFound: 'Service not found',
    serviceNotFoundAr: 'لم يتم العثور على الخدمة',
    locationRequired: 'Location is required',
    locationRequiredAr: 'الموقع مطلوب',
    networkError: 'Network error. Please try again.',
    networkErrorAr: 'خطأ في الشبكة. الرجاء المحاولة مرة أخرى.',
  },
} as const;

export const META_TAGS = {
  // SEO and social media metadata
  title: `${BRAND.name} - ${BRAND.tagline}`,
  titleTemplate: '%s | Dawr',
  description: BRAND.description,
  keywords: [
    'automotive services',
    'car repair',
    'car maintenance',
    'tire services',
    'emergency roadside',
    'car wash',
    'auto service',
    'local car services',
    'car services near me',
    'auto repair nearby',
    'vehicle maintenance',
  ].join(', '),
  
  // Arabic SEO
  titleAr: `${BRAND.nameAr} - ${BRAND.taglineAr}`,
  descriptionAr: BRAND.descriptionAr,
  keywordsAr: [
    'خدمات السيارات',
    'إصلاح السيارات',
    'صيانة السيارات',
    'خدمات الإطارات',
    'المساعدة على الطريق',
    'غسيل السيارات',
    'خدمة السيارات',
    'خدمات السيارات القريبة',
    'إصلاح السيارات بالقرب مني',
    'صيانة المركبات',
  ].join('، '),
  
  // Open Graph
  og: {
    siteName: BRAND.fullName,
    type: 'website',
    locale: 'en_US',
    localeAlternate: 'ar',
    image: '/logo512.png',
    imageAlt: `${BRAND.name} - Automotive Services Platform`,
  },
  
  // Twitter Card
  twitter: {
    card: 'summary_large_image',
    site: BRAND.social.twitter,
    creator: BRAND.social.twitter,
  },
} as const;
